import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import {
  ChevronDown,
  ChevronRight,
  File,
  Files,
  Database,
  Activity,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  RefreshCw,
  Search,
  Filter,
  ChevronLeft,
  FileText,
  X,
} from 'lucide-react';

const ITEMS_PER_PAGE = 20;

const OptimizedDocumentDashboard = () => {
  const [selectedCMO, setSelectedCMO] = useState('all');
  const [files, setFiles] = useState({});
  const [expandedFiles, setExpandedFiles] = useState(new Set());
  const [expandedSplits, setExpandedSplits] = useState(new Set());
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSplit, setSelectedSplit] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('receivedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const wsRef = useRef(null);

  // Configuration
  const API_BASE_URL =
    'https://s679mr8g8i.execute-api.us-west-2.amazonaws.com/dev';
  const WS_ENDPOINT =
    'wss://ftdfkyo97g.execute-api.us-west-2.amazonaws.com/dev/';

  // Memoized status styles
  const statusStyles = useMemo(
    () => ({
      processed: {
        color: '#059669',
        backgroundColor: '#ecfdf5',
        borderColor: '#a7f3d0',
      },
      processing: {
        color: '#2563eb',
        backgroundColor: '#eff6ff',
        borderColor: '#93c5fd',
      },
      queued: {
        color: '#d97706',
        backgroundColor: '#fffbeb',
        borderColor: '#fcd34d',
      },
      failed: {
        color: '#dc2626',
        backgroundColor: '#fef2f2',
        borderColor: '#fca5a5',
      },
      default: {
        color: '#4b5563',
        backgroundColor: '#f9fafb',
        borderColor: '#d1d5db',
      },
    }),
    []
  );

  const getStatusStyle = useCallback(
    (status) => {
      return statusStyles[status?.toLowerCase()] || statusStyles.default;
    },
    [statusStyles]
  );

  const getStatusIcon = useCallback((status) => {
    const iconStyle = { width: '16px', height: '16px' };
    switch (status?.toLowerCase()) {
      case 'processed':
        return <CheckCircle style={iconStyle} />;
      case 'processing':
        return (
          <Activity
            style={{ ...iconStyle, animation: 'spin 1s linear infinite' }}
          />
        );
      case 'queued':
        return <Clock style={iconStyle} />;
      case 'failed':
        return <XCircle style={iconStyle} />;
      default:
        return <AlertCircle style={iconStyle} />;
    }
  }, []);

  // Helper function to format extracted data
  const formatExtractedData = (data) => {
    if (!data) return null;

    const formatValue = (value) => {
      if (value === null || value === undefined) return 'N/A';
      if (typeof value === 'object' && !Array.isArray(value)) {
        // Check if it's a confidence object
        if (value.value !== undefined) {
          const confidence = value.confidence
            ? ` (${parseFloat(value.confidence).toFixed(1)}% confidence)`
            : '';
          return `${value.value || 'N/A'}${confidence}`;
        }
        return JSON.stringify(value, null, 2);
      }
      if (Array.isArray(value)) {
        return value.map((item, index) => (
          <div key={index} style={{ marginLeft: '20px', marginTop: '8px' }}>
            <strong>Item {index + 1}:</strong>
            {formatValue(item)}
          </div>
        ));
      }
      return String(value);
    };

    const formatKey = (key) => {
      // Convert snake_case to Title Case
      return key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    return Object.entries(data)
      .map(([key, value]) => {
        // Skip certain system fields
        if (['s3_location', 'folder_name', 'filename'].includes(key)) {
          return null;
        }

        return (
          <div key={key} style={{ marginBottom: '12px' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '8px',
                backgroundColor: '#f9fafb',
                borderRadius: '4px',
                border: '1px solid #e5e7eb',
              }}
            >
              <span
                style={{
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.875rem',
                  marginBottom: '4px',
                }}
              >
                {formatKey(key)}:
              </span>
              <span
                style={{
                  color: '#111827',
                  fontSize: '0.875rem',
                  wordBreak: 'break-word',
                }}
              >
                {formatValue(value)}
              </span>
            </div>
          </div>
        );
      })
      .filter(Boolean);
  };

  // Processed files with memoization
  const processedFiles = useMemo(() => {
    const fileArray = Object.values(files);

    // Apply filters
    let filtered = fileArray.filter((file) => {
      // CMO filter
      if (
        selectedCMO !== 'all' &&
        file.cmo.toLowerCase() !== selectedCMO.toLowerCase()
      ) {
        return false;
      }

      // Status filter
      if (statusFilter !== 'all' && file.status !== statusFilter) {
        return false;
      }

      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          file.originalFileName?.toLowerCase().includes(searchLower) ||
          file.cmo?.toLowerCase().includes(searchLower) ||
          file.site?.toLowerCase().includes(searchLower) ||
          file.emailSubject?.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'receivedAt':
          aVal = new Date(a.receivedAt || 0);
          bVal = new Date(b.receivedAt || 0);
          break;
        case 'fileName':
          aVal = a.originalFileName || '';
          bVal = b.originalFileName || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'cmo':
          aVal = a.cmo || '';
          bVal = b.cmo || '';
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  }, [files, selectedCMO, statusFilter, searchTerm, sortBy, sortOrder]);

  // Paginated data
  const paginatedFiles = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return processedFiles.slice(startIndex, endIndex);
  }, [processedFiles, currentPage]);

  // Stats calculation
  const stats = useMemo(() => {
    const totalFiles = processedFiles.length;
    const totalSplits = processedFiles.reduce(
      (acc, file) => acc + Object.keys(file.splits || {}).length,
      0
    );
    const processedFiles_count = processedFiles.filter(
      (f) => f.status === 'processed'
    ).length;
    const processingFiles_count = processedFiles.filter(
      (f) => f.status === 'processing'
    ).length;
    const queuedFiles_count = processedFiles.filter(
      (f) => f.status === 'queued'
    ).length;
    const failedFiles_count = processedFiles.filter(
      (f) => f.status === 'failed'
    ).length;

    return {
      totalFiles,
      totalSplits,
      processedFiles: processedFiles_count,
      processingFiles: processingFiles_count,
      queuedFiles: queuedFiles_count,
      failedFiles: failedFiles_count,
    };
  }, [processedFiles]);

  // Pagination helpers
  const totalPages = Math.ceil(processedFiles.length / ITEMS_PER_PAGE);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  // Load data
  const loadRealData = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/files?cmo=${selectedCMO}`, {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setFiles(data);
      setLastUpdate(new Date().toISOString());
      setCurrentPage(1);
      setConnectionStatus('connected');
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Error loading data:', error);
      setError(error.message);
      setConnectionStatus('error');
    } finally {
      setLoading(false);
    }
  }, [selectedCMO]);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    try {
      wsRef.current = new WebSocket(WS_ENDPOINT);

      wsRef.current.onopen = () => {
        setConnectionStatus('connected');
        console.log('WebSocket connected');
      };

      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        handleRealtimeUpdate(data);
      };

      wsRef.current.onclose = () => {
        setConnectionStatus('disconnected');
        setTimeout(connectWebSocket, 3000);
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('error');
      };
    } catch (error) {
      console.error('WebSocket connection failed:', error);
      setConnectionStatus('error');
    }
  }, []);

  // Handle real-time updates
  const handleRealtimeUpdate = useCallback((data) => {
    if (data.type === 'DOC_EVENT') {
      setFiles((prev) => {
        const updated = { ...prev };
        const file = updated[data.fileId];

        if (file?.splits) {
          Object.keys(file.splits).forEach((splitId) => {
            const split = file.splits[splitId];
            if (split.s3Key.includes(data.fileId) || splitId === data.splitId) {
              split.status = data.status;
              split.endTime = data.at;
            }
          });

          const allSplitStatuses = Object.values(file.splits).map(
            (s) => s.status
          );
          if (allSplitStatuses.every((s) => s === 'processed')) {
            file.status = 'processed';
          } else if (allSplitStatuses.some((s) => s === 'processing')) {
            file.status = 'processing';
          }
        }

        return updated;
      });

      setLastUpdate(new Date().toISOString());
    }
  }, []);

  // Event handlers
  const handleRefresh = useCallback(() => {
    loadRealData();
  }, [loadRealData]);

  const toggleFileExpansion = useCallback((fileId) => {
    setExpandedFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  }, []);

  const toggleSplitExpansion = useCallback((splitKey) => {
    setExpandedSplits((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(splitKey)) {
        newSet.delete(splitKey);
      } else {
        newSet.add(splitKey);
      }
      return newSet;
    });
  }, []);

  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleCMOChange = useCallback((e) => {
    setSelectedCMO(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleStatusFilterChange = useCallback((e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleStatCardClick = useCallback((filterType) => {
    setStatusFilter(filterType);
    setCurrentPage(1);
  }, []);

  // Effects
  useEffect(() => {
    loadRealData();
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [loadRealData, connectWebSocket]);

  // Modal Component for Extracted Data
  const ExtractedDataModal = ({ split, onClose }) => {
    if (!split) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Modal Header */}
          <div
            style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <h3
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#111827',
                  margin: 0,
                }}
              >
                Extracted Data
              </h3>
              <p
                style={{
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  margin: '4px 0 0 0',
                }}
              >
                {split.docType || split.splitId} -{' '}
                {split.s3Key?.split('/').pop()}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                padding: '8px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '6px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = '#f3f4f6')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = 'transparent')
              }
            >
              <X style={{ width: '20px', height: '20px', color: '#6b7280' }} />
            </button>
          </div>

          {/* Modal Body */}
          <div
            style={{
              padding: '24px',
              overflowY: 'auto',
              flex: 1,
            }}
          >
            {split.extractedData ? (
              <div>{formatExtractedData(split.extractedData)}</div>
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#6b7280',
                }}
              >
                <AlertCircle
                  style={{
                    width: '48px',
                    height: '48px',
                    margin: '0 auto 16px',
                    color: '#9ca3af',
                  }}
                />
                <p>No extracted data available for this document</p>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = '#1d4ed8')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = '#2563eb')
              }
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // File Item Component
  const FileItem = React.memo(({ file }) => {
    const isExpanded = expandedFiles.has(file.fileId);
    const splitsArray = Object.values(file.splits || {});

    return (
      <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '8px',
            transition: 'background-color 0.2s',
          }}
          onClick={() => toggleFileExpansion(file.fileId)}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = '#f9fafb')
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = 'transparent')
          }
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {isExpanded ? (
              <ChevronDown
                style={{ width: '20px', height: '20px', color: '#6b7280' }}
              />
            ) : (
              <ChevronRight
                style={{ width: '20px', height: '20px', color: '#6b7280' }}
              />
            )}
            <File style={{ width: '24px', height: '24px', color: '#2563eb' }} />
            <div>
              <h3 style={{ fontWeight: '500', color: '#111827', margin: 0 }}>
                {file.originalFileName}
              </h3>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                {file.cmo} • {file.site} • {splitsArray.length} documents
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: '500',
                border: '1px solid',
                ...getStatusStyle(file.status),
              }}
            >
              {getStatusIcon(file.status)}
              {file.status}
            </span>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {file.receivedAt
                ? new Date(file.receivedAt).toLocaleDateString()
                : 'Unknown'}
            </span>
          </div>
        </div>

        {isExpanded && (
          <div style={{ marginLeft: '32px', marginTop: '16px' }}>
            <div
              style={{
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
              }}
            >
              <h4
                style={{
                  fontWeight: '500',
                  color: '#111827',
                  marginBottom: '8px',
                }}
              >
                Email Details
              </h4>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '16px',
                  fontSize: '0.875rem',
                }}
              >
                <div>
                  <span style={{ fontWeight: '500', color: '#6b7280' }}>
                    Subject:
                  </span>
                  <span style={{ marginLeft: '8px', color: '#111827' }}>
                    {file.emailSubject || 'N/A'}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: '500', color: '#6b7280' }}>
                    From:
                  </span>
                  <span style={{ marginLeft: '8px', color: '#111827' }}>
                    {file.senderEmail || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4
                style={{
                  fontWeight: '500',
                  color: '#111827',
                  marginBottom: '12px',
                }}
              >
                Split Documents ({splitsArray.length} documents)
              </h4>
              {splitsArray.map((split, index) => {
                const splitKey = `${file.fileId}-${split.splitId}`;
                const isExpanded = expandedSplits.has(splitKey);

                return (
                  <div
                    key={split.splitId}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      backgroundColor:
                        split.splitId === 'source' ? '#f8fafc' : 'white',
                    }}
                  >
                    <div
                      style={{
                        padding: '16px',
                        cursor: split.extractedData ? 'pointer' : 'default',
                        transition: 'background-color 0.2s',
                      }}
                      onClick={() =>
                        split.extractedData && toggleSplitExpansion(splitKey)
                      }
                      onMouseEnter={(e) => {
                        if (split.extractedData) {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '8px',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          {split.extractedData &&
                            (isExpanded ? (
                              <ChevronDown
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  color: '#6b7280',
                                }}
                              />
                            ) : (
                              <ChevronRight
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  color: '#6b7280',
                                }}
                              />
                            ))}
                          <div
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              backgroundColor:
                                split.splitId === 'source'
                                  ? '#3b82f6'
                                  : '#7c3aed',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                            }}
                          >
                            {split.splitId === 'source'
                              ? 'SRC'
                              : index.toString()}
                          </div>
                          <span style={{ fontWeight: '500', color: '#111827' }}>
                            {split.splitId === 'source'
                              ? '📄 Original Document'
                              : `📋 ${split.docType || split.splitId}`}
                          </span>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                          }}
                        >
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              borderRadius: '9999px',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              border: '1px solid',
                              ...getStatusStyle(split.status),
                            }}
                          >
                            {getStatusIcon(split.status)}
                            {split.status}
                          </span>
                          {split.extractedData && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedSplit(split);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 8px',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                transition: 'background-color 0.2s',
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  '#2563eb')
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  '#3b82f6')
                              }
                            >
                              <FileText
                                style={{ width: '14px', height: '14px' }}
                              />
                              View Data
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Inline extracted data preview when expanded */}
                      {isExpanded && split.extractedData && (
                        <div
                          style={{
                            marginTop: '16px',
                            padding: '12px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '6px',
                            maxHeight: '300px',
                            overflowY: 'auto',
                          }}
                        >
                          <h5
                            style={{
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#374151',
                              marginBottom: '12px',
                            }}
                          >
                            Extracted Fields:
                          </h5>
                          <div style={{ fontSize: '0.75rem' }}>
                            {formatExtractedData(split.extractedData)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  });

  // Styles
  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: '#f9fafb',
    padding: '24px',
  };

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    border: '1px solid #e5e7eb',
    padding: '24px',
    marginBottom: '24px',
  };

  const inputStyle = {
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    width: '100%',
  };

  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  };

  return (
    <div style={containerStyle}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        {/* Header */}
        <div style={cardStyle}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px',
            }}
          >
            <h1
              style={{
                fontSize: '1.875rem',
                fontWeight: 'bold',
                color: '#111827',
                margin: 0,
              }}
            >
              Document Pipeline Dashboard
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button
                onClick={handleRefresh}
                disabled={loading}
                style={{
                  ...buttonStyle,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                <RefreshCw
                  style={{
                    width: '16px',
                    height: '16px',
                    animation: loading ? 'spin 1s linear infinite' : 'none',
                  }}
                />
                {loading ? 'Loading...' : 'Refresh'}
              </button>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '4px 12px',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  backgroundColor:
                    connectionStatus === 'connected'
                      ? '#dcfce7'
                      : connectionStatus === 'error'
                      ? '#fee2e2'
                      : '#fef3c7',
                  color:
                    connectionStatus === 'connected'
                      ? '#166534'
                      : connectionStatus === 'error'
                      ? '#991b1b'
                      : '#92400e',
                }}
              >
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor:
                      connectionStatus === 'connected'
                        ? '#16a34a'
                        : connectionStatus === 'error'
                        ? '#dc2626'
                        : '#d97706',
                  }}
                />
                {connectionStatus === 'connected'
                  ? 'API Connected'
                  : connectionStatus === 'error'
                  ? 'API Error'
                  : 'Connecting...'}
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div
              style={{
                padding: '12px',
                backgroundColor: '#fee2e2',
                border: '1px solid #fca5a5',
                borderRadius: '6px',
                marginBottom: '16px',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <XCircle
                  style={{ width: '16px', height: '16px', color: '#dc2626' }}
                />
                <span style={{ color: '#dc2626', fontWeight: '500' }}>
                  Error: {error}
                </span>
              </div>
            </div>
          )}

          {/* Filters */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '16px',
            }}
          >
            <div>
              <label
                style={{
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Search Files
              </label>
              <div style={{ position: 'relative' }}>
                <Search
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '16px',
                    height: '16px',
                    color: '#6b7280',
                  }}
                />
                <input
                  type="text"
                  placeholder="Search by filename, CMO, site..."
                  value={searchTerm}
                  onChange={handleSearch}
                  style={{ ...inputStyle, paddingLeft: '40px' }}
                />
              </div>
            </div>

            <div>
              <label
                style={{
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Filter by CMO
              </label>
              <select
                value={selectedCMO}
                onChange={handleCMOChange}
                style={inputStyle}
              >
                <option value="all">All CMOs</option>
                <option value="patheon">Patheon</option>
                <option value="pci">PCI</option>
              </select>
            </div>

            <div>
              <label
                style={{
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Filter by Status
              </label>
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                style={inputStyle}
              >
                <option value="all">All Statuses</option>
                <option value="processed">Processed</option>
                <option value="processing">Processing</option>
                <option value="queued">Queued</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div>
              <label
                style={{
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Sort By
              </label>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                  setCurrentPage(1);
                }}
                style={inputStyle}
              >
                <option value="receivedAt-desc">Newest First</option>
                <option value="receivedAt-asc">Oldest First</option>
                <option value="fileName-asc">Filename A-Z</option>
                <option value="fileName-desc">Filename Z-A</option>
                <option value="status-asc">Status A-Z</option>
              </select>
            </div>
          </div>

          {/* Results info */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '6px',
              fontSize: '0.875rem',
              color: '#6b7280',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span>
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}-
                {Math.min(currentPage * ITEMS_PER_PAGE, processedFiles.length)}{' '}
                of {processedFiles.length} files
              </span>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div>
          <h2
            style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '12px',
            }}
          >
            Quick Stats
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '24px',
            }}
          >
            {/* Stats cards remain the same */}
            <div
              style={{
                ...cardStyle,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onClick={() => handleStatCardClick('all')}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Files
                  style={{
                    width: '32px',
                    height: '32px',
                    color: '#2563eb',
                  }}
                />
                <div style={{ marginLeft: '16px' }}>
                  <p
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#6b7280',
                      margin: 0,
                    }}
                  >
                    Total Files
                  </p>
                  <p
                    style={{
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      color: '#111827',
                      margin: 0,
                    }}
                  >
                    {stats.totalFiles}
                  </p>
                </div>
              </div>
            </div>

            <div
              style={{
                ...cardStyle,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onClick={() => handleStatCardClick('processed')}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle
                  style={{
                    width: '32px',
                    height: '32px',
                    color: '#059669',
                  }}
                />
                <div style={{ marginLeft: '16px' }}>
                  <p
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#6b7280',
                      margin: 0,
                    }}
                  >
                    Processed
                  </p>
                  <p
                    style={{
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      color: '#111827',
                      margin: 0,
                    }}
                  >
                    {stats.processedFiles}
                  </p>
                </div>
              </div>
            </div>

            <div
              style={{
                ...cardStyle,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onClick={() => handleStatCardClick('processing')}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Activity
                  style={{
                    width: '32px',
                    height: '32px',
                    color: '#2563eb',
                  }}
                />
                <div style={{ marginLeft: '16px' }}>
                  <p
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#6b7280',
                      margin: 0,
                    }}
                  >
                    Processing
                  </p>
                  <p
                    style={{
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      color: '#111827',
                      margin: 0,
                    }}
                  >
                    {stats.processingFiles}
                  </p>
                </div>
              </div>
            </div>

            <div
              style={{
                ...cardStyle,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onClick={() => handleStatCardClick('queued')}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Clock
                  style={{
                    width: '32px',
                    height: '32px',
                    color: '#d97706',
                  }}
                />
                <div style={{ marginLeft: '16px' }}>
                  <p
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#6b7280',
                      margin: 0,
                    }}
                  >
                    Queued
                  </p>
                  <p
                    style={{
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      color: '#111827',
                      margin: 0,
                    }}
                  >
                    {stats.queuedFiles}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Files List */}
        <div style={cardStyle}>
          <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
            <h2
              style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#111827',
                margin: 0,
              }}
            >
              Document Files {loading && '(Loading...)'}
            </h2>
          </div>

          {paginatedFiles.length > 0 ? (
            <>
              <div>
                {paginatedFiles.map((file) => (
                  <FileItem key={file.fileId} file={file} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '24px',
                    borderTop: '1px solid #e5e7eb',
                  }}
                >
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!hasPrevPage}
                    style={{
                      ...buttonStyle,
                      opacity: hasPrevPage ? 1 : 0.5,
                      cursor: hasPrevPage ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <ChevronLeft style={{ width: '16px', height: '16px' }} />
                    Previous
                  </button>

                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNextPage}
                    style={{
                      ...buttonStyle,
                      opacity: hasNextPage ? 1 : 0.5,
                      cursor: hasNextPage ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Next
                    <ChevronRight style={{ width: '16px', height: '16px' }} />
                  </button>
                </div>
              )}
            </>
          ) : loading ? (
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <RefreshCw
                style={{
                  width: '48px',
                  height: '48px',
                  color: '#9ca3af',
                  margin: '0 auto 16px',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <h3
                style={{
                  fontSize: '1.125rem',
                  fontWeight: '500',
                  color: '#111827',
                  marginBottom: '8px',
                }}
              >
                Loading documents...
              </h3>
            </div>
          ) : (
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <Files
                style={{
                  width: '48px',
                  height: '48px',
                  color: '#9ca3af',
                  margin: '0 auto 16px',
                }}
              />
              <h3
                style={{
                  fontSize: '1.125rem',
                  fontWeight: '500',
                  color: '#111827',
                  marginBottom: '8px',
                }}
              >
                No documents found
              </h3>
              <p style={{ color: '#6b7280', margin: 0 }}>
                {searchTerm
                  ? `No documents match "${searchTerm}"`
                  : 'No documents match your filters'}
              </p>
            </div>
          )}
        </div>

        {/* Modal for extracted data */}
        {selectedSplit && (
          <ExtractedDataModal
            split={selectedSplit}
            onClose={() => setSelectedSplit(null)}
          />
        )}
      </div>

      {/* Add animation keyframes */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default OptimizedDocumentDashboard;

